// Chamadas do Plano de ação (ações `pa_*` em /comercial/propostas-geradas).
// Contrato: docs/plano-acao.md (se divergir, vale o doc). Uma função por ação;
// o erro vem com a frase do servidor (`erroDe`), que é o que a tela mostra.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

async function post<T = any>(body: Record<string, any>, timeout = 30000): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token }, timeout });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

export const erroDe = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'erro desconhecido';

/** 409 = outra pessoa mexeu antes (revisão otimista). */
export const ehConflito = (e: any): boolean => e?.response?.status === 409;
/** 403 = sem `gestao.plano_acao.ver`. */
export const ehSemAcesso = (e: any): boolean => e?.response?.status === 403;

export type StatusEtapa = 'nao_iniciada' | 'em_andamento' | 'concluida' | 'travada';
export type StatusTarefa = 'pendente' | 'em_andamento' | 'concluida' | 'cancelada';
export type StatusInfo = 'pendente' | 'em_andamento' | 'pronto';
export type FonteInfo = 'erp' | 'levantar' | 'decisao';

export interface Ciclo {
  id: number; titulo: string; consultoria: string | null; pergunta: string | null;
  inicio: string; fim: string; status: 'rascunho' | 'ligado' | 'encerrado'; modelo: string | null; revisao: number;
}
export type CicloResumo = Ciclo;
export interface Papel { papel: string; rotulo: string; email: string | null; nome: string | null }
export interface Tarefa {
  id: number; etapa_id: number; ordem: number; titulo: string; descricao: string | null;
  papel_responsavel: string | null; papeis_observadores: string[]; prazo: string | null;
  atividade_id: number | null;
  /** Vem da atividade (public.atividades.status); nulo enquanto o ciclo não foi ligado. */
  status: StatusTarefa | null; revisao: number;
}
export interface Informacao {
  id: number; etapa_id: number | null; ordem: number; item: string; fonte: FonteInfo; onde: string | null;
  papel_responsavel: string | null; status: StatusInfo; link: string | null; observacao: string | null; revisao: number;
}
export interface Etapa {
  id: number; numero: number; titulo: string; inicio: string; fim: string;
  decisao_numero: number | null; decisao_titulo: string | null;
  o_que_fazer: string | null; como_fazer: string | null; entrega: string | null; quem_faz: string | null; tempo_estimado: string | null;
  papel_dono_decisao: string | null; status: StatusEtapa; motivo_trava: string | null;
  decisao_texto: string | null; decisao_em: string | null; decisao_por: string | null; revisao: number;
  tarefas: Tarefa[]; informacoes: Informacao[];
}
export interface Indicador {
  id: number; ordem: number; nome: string; como_mede: string | null; fonte: 'manual' | 'automatico';
  linha_base: string | null; linha_base_nota: string | null; meta: string | null; valor_atual: string | null;
  leitura_em: string | null; revisao: number;
}
export interface Marco { id: number; ordem: number; titulo: string; data_prevista: string | null; feito_em: string | null; observacao: string | null; revisao: number }
export interface Permissoes { pode_editar: boolean; pode_gerir: boolean; email: string; papeis_do_usuario: string[] }
export interface CicloCompleto {
  ciclo: Ciclo; papeis: Papel[]; etapas: Etapa[]; antes: Informacao[]; indicadores: Indicador[]; marcos: Marco[]; permissoes: Permissoes;
}
export interface Conta { email: string; nome: string }

const lista = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

/**
 * O servidor pode mandar tarefas/informações aninhadas na etapa ou soltas no
 * topo (com `etapa_id`); a tela aceita os dois jeitos.
 */
export function normalizarCiclo(d: any): CicloCompleto {
  const soltasT = lista<Tarefa>(d?.tarefas);
  const soltasI = lista<Informacao>(d?.informacoes);
  const etapas = lista<Etapa>(d?.etapas).map((e) => ({
    ...e,
    tarefas: lista<Tarefa>(e.tarefas).concat(soltasT.filter((t) => t.etapa_id === e.id))
      .map((t) => ({ ...t, papeis_observadores: lista<string>(t.papeis_observadores), status: t.status ?? null }))
      .sort((a, b) => a.ordem - b.ordem),
    informacoes: lista<Informacao>(e.informacoes).concat(soltasI.filter((i) => i.etapa_id === e.id)).sort((a, b) => a.ordem - b.ordem),
  })).sort((a, b) => a.numero - b.numero);
  const antes = lista<Informacao>(d?.antes).concat(soltasI.filter((i) => i.etapa_id == null)).sort((a, b) => a.ordem - b.ordem);
  const p = d?.permissoes || {};
  return {
    ciclo: d.ciclo, papeis: lista<Papel>(d?.papeis), etapas, antes,
    indicadores: lista<Indicador>(d?.indicadores).sort((a, b) => a.ordem - b.ordem),
    marcos: lista<Marco>(d?.marcos).sort((a, b) => a.ordem - b.ordem),
    permissoes: {
      pode_editar: !!p.pode_editar, pode_gerir: !!p.pode_gerir, email: String(p.email || '').toLowerCase(),
      papeis_do_usuario: lista<string>(p.papeis_do_usuario),
    },
  };
}

/** `pode_gerir` vem de `permissoes` na listagem; se não vier, fica nulo (desconhecido) e a tela NÃO mostra botão de gestor. */
export async function listarCiclos(): Promise<{ ciclos: CicloResumo[]; pode_gerir: boolean | null }> {
  const d = await post({ action: 'pa_listar_ciclos' });
  const pg = d?.permissoes?.pode_gerir;
  return { ciclos: lista<CicloResumo>(d?.ciclos ?? d), pode_gerir: typeof pg === 'boolean' ? pg : null };
}
export async function obterCiclo(ciclo_id: number): Promise<CicloCompleto> {
  return normalizarCiclo(await post({ action: 'pa_obter_ciclo', ciclo_id }));
}
export const importarModelo = (modelo: string, inicio: string) => post({ action: 'pa_importar_modelo', modelo, inicio });
export const ligarCiclo = (ciclo_id: number) => post({ action: 'pa_ligar_ciclo', ciclo_id });
export async function listarContas(): Promise<Conta[]> {
  const d = await post({ action: 'pa_contas' });
  return lista<Conta>(d?.contas ?? d);
}

// Escritas: sempre com a `revisao` que a tela leu (409 se mudou).
export const salvarEtapa = (id: number, revisao: number, campos: Partial<Pick<Etapa, 'status' | 'motivo_trava' | 'decisao_texto' | 'decisao_em'>>) =>
  post({ action: 'pa_salvar_etapa', id, revisao, ...campos });
/** `statusVisto`: o andamento que a tela mostrava; o servidor responde 409 se a atividade já mudou. */
export const salvarTarefa = (id: number, revisao: number, campos: Partial<Pick<Tarefa, 'status' | 'titulo' | 'descricao' | 'prazo' | 'papel_responsavel'>>, statusVisto?: StatusTarefa | null) =>
  post({ action: 'pa_salvar_tarefa', id, revisao, ...campos, ...(statusVisto !== undefined ? { status_visto: statusVisto } : {}) });
export const criarTarefa = (etapa_id: number, campos: Pick<Tarefa, 'titulo' | 'descricao' | 'prazo' | 'papel_responsavel'>) =>
  post({ action: 'pa_criar_tarefa', etapa_id, ...campos });
export const salvarInformacao = (id: number, revisao: number, campos: Partial<Pick<Informacao, 'status' | 'link' | 'observacao'>>) =>
  post({ action: 'pa_salvar_informacao', id, revisao, ...campos });
export const salvarIndicador = (id: number, revisao: number, campos: Partial<Pick<Indicador, 'meta' | 'valor_atual' | 'leitura_em'>>) =>
  post({ action: 'pa_salvar_indicador', id, revisao, ...campos });
export const salvarMarco = (id: number, revisao: number, campos: Partial<Pick<Marco, 'data_prevista' | 'feito_em' | 'observacao'>>) =>
  post({ action: 'pa_salvar_marco', id, revisao, ...campos });
export const salvarPapel = (ciclo_id: number, papel: string, email: string) =>
  post({ action: 'pa_salvar_papel', ciclo_id, papel, email });
